import React, { useEffect, useRef } from 'react';
import { Animated, Easing, StyleSheet, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { Colors, Radius, Shadow } from '../../theme';

const DOT_COUNT = 3;
const BOUNCE_DURATION_MS = 380;
const STAGGER_MS = 120;

function useDotAnim(delayMs: number) {
  const value = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    let cancelled = false;
    const loop = () => {
      if (cancelled) return;
      Animated.sequence([
        Animated.delay(delayMs),
        Animated.timing(value, { toValue: 1, duration: BOUNCE_DURATION_MS, easing: Easing.out(Easing.quad), useNativeDriver: true }),
        Animated.timing(value, { toValue: 0, duration: BOUNCE_DURATION_MS, easing: Easing.in(Easing.quad), useNativeDriver: true }),
      ]).start(({ finished }) => {
        if (finished) loop();
      });
    };
    loop();
    return () => { cancelled = true; };
  }, [delayMs, value]);
  return value;
}

/** Premium glass capsule with an animated 3-dot bounce — used in the chat header and (optionally) inline in the message list. */
const TypingIndicator: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const dot0 = useDotAnim(0);
  const dot1 = useDotAnim(STAGGER_MS);
  const dot2 = useDotAnim(STAGGER_MS * 2);
  const dots = [dot0, dot1, dot2];

  return (
    <View style={[styles.capsule, compact && styles.capsuleCompact]}>
      <LinearGradient
        pointerEvents="none"
        colors={['rgba(255,255,255,0.14)', 'rgba(255,255,255,0)']}
        start={{ x: 0.1, y: 0 }}
        end={{ x: 0.8, y: 1 }}
        style={StyleSheet.absoluteFill}
      />
      {Array.from({ length: DOT_COUNT }).map((_, index) => (
        <Animated.View
          key={index}
          style={[
            styles.dot,
            {
              opacity: dots[index].interpolate({ inputRange: [0, 1], outputRange: [0.35, 1] }),
              transform: [{ translateY: dots[index].interpolate({ inputRange: [0, 1], outputRange: [0, -3] }) }],
            },
          ]}
        />
      ))}
    </View>
  );
};

export default TypingIndicator;

const styles = StyleSheet.create({
  capsule: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(10,14,22,0.6)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
    overflow: 'hidden',
    ...Shadow.card,
    shadowColor: Colors.turquoise,
    shadowOpacity: 0.2,
  },
  capsuleCompact: {
    paddingHorizontal: 8,
    paddingVertical: 5,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: 'rgba(255,255,255,0.85)',
  },
});
